import React, { useMemo, useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FaClock, FaUserAlt } from 'react-icons/fa';
import { IoCloseCircle } from 'react-icons/io5';
import { HiQueueList } from 'react-icons/hi2';
import { AgentProfilePictureByLogin } from 'components/AgentProfilePictureByLogin';

// Container principal para o card do agente
const AgentCardContainer = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  background-color: ${({ backgroundColor }) => backgroundColor};
  border-radius: ${({ borderRadius }) => `calc(${borderRadius}/2)`};
  color: white;
  row-gap: 0.5rem;
`;

// Container para informações do agente
const AgentInfoContainer = styled.div`
  display: flex;
  column-gap: 0.5rem;
  padding-block-end: 0.5rem;
  border-bottom: 2px solid hsla(0, 0%, 100%, 0.2);
`;

// Detalhes do agente
const AgentDetails = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 0.25rem;
  overflow: hidden;
`;

// Nome do agente
const AgentName = styled.h3`
  margin-block: 0;
  color: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1rem;
  max-width: 200px;
`;

// Status de pausa do agente
const AgentPauseName = styled.h4`
  color: inherit;
  margin-block: 0;
  line-height: 1rem;
  font-weight: 400;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

// Container para informações de fila
const QueueInfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

// Rótulo para informações de fila
const QueueLabel = styled.div`
  margin-block: 0;
  line-height: 1rem;
  font-weight: 500;
  display: flex;
  column-gap: 0.25rem;

  // Ícones transparentes
  &:first-child > :first-child {
    opacity: 65%;
  }
`;

// Tempo de fila
const QueueTime = styled.span`
  margin-block: 0;
  line-height: 1rem;
  font-weight: 700;
`;

/**
 * Formata a diferença de tempo em milissegundos para o formato "HH:MMh"
 * @param {number} diffInMs - Diferença de tempo em milissegundos
 * @returns {string} String de tempo formatada
 */
const formatTimeDiff = (diffInMs) => {
  if (!diffInMs || diffInMs <= 0) return '00h:00min';

  const hours = Math.floor(diffInMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));

  return `${hours.toString().padStart(2, '0')}h:${minutes
    .toString()
    .padStart(2, '0')}min`;
};

/**
 * MonitoringAgentCard - Componente para exibição de informações de monitoramento do agente
 *
 * @param {Object} props - Propriedades do componente
 * @param {Object} props.agent - Objeto contendo todos os dados do agente
 * @param {string} props.borderRadius - Raio da borda da configuração do sistema
 * @returns {JSX.Element} Componente renderizado
 */
export const MonitoringAgentCard = React.memo(({ agent, borderRadius }) => {
  const [queueTime, setQueueTime] = useState('00h:00min');

  const {
    agentName,
    agentLogin,
    queueTimestamp,
    attributed,
    active,
    closed,
    pauseName,
    backgroundColor,
    queues,
    queueName
  } = agent;

  // Obter apenas a primeira fila do agente, lidando com formatos legado e novo
  const firstQueueName = useMemo(() => {
    // Se tiver array de queues, use a primeira
    if (queues && Array.isArray(queues) && queues.length > 0) {
      const validQueues = queues.map((queue) => queue.name).filter(Boolean);
      return validQueues.length > 0 ? validQueues[0] : null;
    }
    // Caso contrário, verifique se há uma única queueName legada
    if (queueName) {
      return queueName;
    }
    return null;
  }, [queues, queueName]);

  const calculateQueueTime = useCallback(() => {
    if (!queueTimestamp) {
      return '00h:00min';
    }

    const now = new Date();
    const queueStart = new Date(queueTimestamp);
    const diffInMs = now - queueStart;

    return formatTimeDiff(diffInMs);
  }, [queueTimestamp]);

  useEffect(() => {
    if (!queueTimestamp) {
      setQueueTime('00h:00min');
      return;
    }

    setQueueTime(calculateQueueTime());

    const intervalId = setInterval(() => {
      setQueueTime(calculateQueueTime());
    }, 60000);

    return () => clearInterval(intervalId);
  }, [queueTimestamp, calculateQueueTime]);

  const agentProfilePictureByLoginStyles = useMemo(
    () => ({
      aspectRatio: '1',
      maxHeight: '2.25rem',
      borderRadius: '100%',
      boxShadow: '0 0 0 1px #00000010'
    }),
    []
  );

  return (
    <AgentCardContainer
      backgroundColor={backgroundColor}
      borderRadius={borderRadius}
    >
      <AgentInfoContainer>
        <AgentProfilePictureByLogin
          login={agentLogin}
          alt={`Foto de ${agentName?.name ?? agentName}`}
          style={agentProfilePictureByLoginStyles}
        />
        <AgentDetails>
          <AgentName title={agentName?.name ?? agentName}>
            {agentName?.name ?? agentName}
          </AgentName>
          <AgentPauseName>
            {agent.status !== 'isAvailable'
              ? `${pauseName}${firstQueueName ? ` - ${firstQueueName}` : ''}`
              : firstQueueName}
          </AgentPauseName>
        </AgentDetails>
      </AgentInfoContainer>

      <div>
        <QueueInfoContainer>
          <QueueLabel>
            <FaClock />
            {agent.status === 'isAvailable' ? (
              <span>Tempo de fila</span>
            ) : (
              <span> Tempo de pausa </span>
            )}
          </QueueLabel>
          <QueueTime>{queueTime}</QueueTime>
        </QueueInfoContainer>
      </div>
      <div>
        <QueueInfoContainer>
          <QueueLabel>
            <HiQueueList />
            <span>Atribuídas</span>
          </QueueLabel>
          <QueueTime>{attributed}</QueueTime>
        </QueueInfoContainer>
      </div>
      <div>
        <QueueInfoContainer>
          <QueueLabel>
            <FaUserAlt />
            <span>Atendimento</span>
          </QueueLabel>
          <QueueTime>{active}</QueueTime>
        </QueueInfoContainer>
      </div>
      <div>
        <QueueInfoContainer>
          <QueueLabel>
            <IoCloseCircle />
            <span>Encerradas</span>
          </QueueLabel>
          <QueueTime>{closed}</QueueTime>
        </QueueInfoContainer>
      </div>
    </AgentCardContainer>
  );
});
MonitoringAgentCard.displayName = 'MonitoringAgentCard';
